import { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../axiosConfig';

export function useInvoices(initialSize = 20) {
    const [invoices, setInvoices] = useState([]);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(initialSize);
    const [totalPages, setTotalPages] = useState(1);
    const [totalElements, setTotalElements] = useState(0);
    const [sorting, setSorting] = useState({ key: null, direction: null });
    const [prevSorting, setPrevSorting] = useState({ key: null, direction: null });
    const [filters, setFilters] = useState({});
    const [prevFilters, setPrevFilters] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    const fetchInvoices = useCallback((pageNum, sizeNum, sort, currentFilters, reset = false) => {
        const queryParams = new URLSearchParams({
            page: pageNum,
            size: sizeNum,
            sortProperty: sort.key || '',
            sortDirection: sort.direction || '',
        });
        
        Object.entries(currentFilters || {}).forEach(([key, value]) => {
            if (value !== '' && value !== null && value !== undefined) {
                queryParams.append(key, value);
            }
        });

        setIsLoading(true);
        axiosInstance.get(`/api/invoices?${queryParams.toString()}`)
            .then(response => response.data)
            .then(data => {
                const items = data.content !== undefined ? data.content : data;
                const total = data.totalElements !== undefined ? data.totalElements : items.length;
                const pages = data.totalPages !== undefined ? data.totalPages : 1;
                
                setInvoices(items);
                setTotalElements(total);
                setTotalPages(pages);
            })
            .catch(error => console.error('Error fetching invoices:', error))
            .finally(() => setIsLoading(false));
    }, []);

    useEffect(() => {
        const filtersChanged = JSON.stringify(prevFilters) !== JSON.stringify(filters);
        const sortingChanged = prevSorting.key !== sorting.key || prevSorting.direction !== sorting.direction;

        if (sortingChanged || filtersChanged) {
            setInvoices([]);
            setPage(0);
            fetchInvoices(0, size, sorting, filters, true);
            setPrevSorting(sorting);
            setPrevFilters(filters);
        } else {
            fetchInvoices(page, size, sorting, filters);
        }
    }, [page, size, sorting, prevSorting, filters, prevFilters, fetchInvoices]);

    const reloadInvoices = useCallback(() => {
        setInvoices([]);
        setPage(0);
        fetchInvoices(0, size, sorting, filters, true);
    }, [size, sorting, filters, fetchInvoices]);

    const createInvoice = useCallback(async (formData) => {
        const token = localStorage.getItem('jwtToken');
        const response = await fetch('/api/invoices', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(formData),
        });
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        window.URL.revokeObjectURL(url);
        reloadInvoices();
    }, [reloadInvoices]);

    const updateInvoice = useCallback(async (invoice) => {
        const payload = {
            ...invoice,
            issuerBankDetailsId: invoice.issuerBankDetailsId || (invoice.issuerBankDetails ? invoice.issuerBankDetails.id : null)
        };
        await axiosInstance.put(`/api/invoices/${invoice.id}`, payload);
        reloadInvoices();
    }, [reloadInvoices]);

    const deleteInvoice = useCallback(async (invoice) => {
        setIsLoading(true);
        try {
            await axiosInstance.delete(`/api/invoices/${invoice.id}`);
            setInvoices(prev => prev.filter(inv => inv.id !== invoice.id));
        } finally {
            setIsLoading(false);
        }
    }, []);

    const generatePdf = useCallback(async (e, invoice) => {
        if (e) e.preventDefault();
        setIsLoading(true);
        try {
            const payload = {
            ...invoice,
            issuerBankDetailsId: invoice.issuerBankDetailsId || (invoice.issuerBankDetails ? invoice.issuerBankDetails.id : null)
        };
        const token = localStorage.getItem('jwtToken');
        const response = await fetch('/api/invoices?save=false', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
        });
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank');
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error generating PDF:', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const downloadPdf = useCallback(async (e, invoice) => {
        if (e) e.preventDefault();
        setIsLoading(true);
        try {
            const payload = {
            ...invoice,
            issuerBankDetailsId: invoice.issuerBankDetailsId || (invoice.issuerBankDetails ? invoice.issuerBankDetails.id : null)
        };
        const token = localStorage.getItem('jwtToken');
        const response = await fetch('/api/invoices?save=false', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
        });
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `invoice${invoice.invoiceNumber}.pdf`;
        document.body.appendChild(a);
        a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading PDF:', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const downloadPdfBulk = useCallback(async (invoicesList) => {
        setIsLoading(true);
        try {
            const payload = invoicesList.map(invoice => ({
            ...invoice,
            issuerBankDetailsId: invoice.issuerBankDetailsId || (invoice.issuerBankDetails ? invoice.issuerBankDetails.id : null)
        }));
        const token = localStorage.getItem('jwtToken');
        const response = await fetch('/api/bulk/invoices', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
        });
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `invoices-bulk.pdf`;
        document.body.appendChild(a);
        a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error bulk downloading PDFs:', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    return {
        invoices,
        isLoading,
        sorting,
        setSorting,
        createInvoice,
        updateInvoice,
        deleteInvoice,
        generatePdf,
        downloadPdf,
        downloadPdfBulk,
        filters,
        setFilters,
        page,
        setPage,
        size,
        setSize,
        totalPages,
        totalElements
    };
}
